const SUPPORTED_LOCALES = ["en", "fr", "fr-CA", "nl"];
function isSupported(value) {
    return SUPPORTED_LOCALES.includes(value);
}
function loadLocaleFiles() {
    // Vite/Vitest: `import.meta.glob` MUST appear as a literal expression — Vite
    // statically replaces the whole call at transform time, so we can't alias it
    // through a variable. Under Webpack, `import.meta.glob` is `undefined` and
    // the typeof check skips this branch entirely.
    if (typeof import.meta.glob === "function") {
        const modules = import.meta.glob("./**/*.json", {
            eager: true,
        });
        return Object.entries(modules).map(([path, mod]) => [path, mod.default]);
    }
    // Webpack: `require.context(...)` MUST also appear as a literal call so
    // webpack's static analyzer recognizes it and emits the matching context
    // module at build time. Aliasing `require` through a local variable used to
    // break detection: webpack inlined the alias inside the surrounding
    // `?.context === "function"` guard and produced
    // `__webpack_require__("…sync recursive")?.context` — always undefined,
    // which silently dropped every translation. The try/catch covers
    // environments where `require` is not defined at runtime.
    try {
        const ctx = require.context("./", true, /\.json$/);
        return ctx.keys().map((key) => [key, ctx(key)]);
    }
    catch {
        return [];
    }
}
// Recursively merge `source` into `target` in place. Object-typed values
// (including arrays, since `typeof [] === "object"`) recurse key-by-key —
// arrays therefore merge by index, with target keeping any tail past
// source.length. Primitives (strings, numbers, booleans, null) overwrite.
// Required because two namespace files can share a top-level key — e.g.
// `oportal.header.frontend` and `oportal.shell.frontend` both define a
// `logout` object with different sub-keys, and a shallow merge would drop
// whichever loaded first. Tolgee/vue-i18n payloads are string-only today, so
// the array-recursion path is unreachable in practice.
function deepMerge(target, source) {
    for (const [key, value] of Object.entries(source)) {
        const existing = target[key];
        if (typeof value === "object" &&
            value !== null &&
            typeof existing === "object" &&
            existing !== null) {
            deepMerge(existing, value);
        }
        else {
            target[key] = value;
        }
    }
}
// One bucket per supported locale. Files get merged into the matching bucket
// regardless of which subfolder they came from, so all module namespaces end
// up flat under their locale (matches the behavior of the previous hand-written
// imports + spread).
export const messages = {
    en: {},
    fr: {},
    "fr-CA": {},
    nl: {},
};
// Walk every JSON the bundler discovered. The locale is the filename stem
// (last path segment before `.json`), so:
//   ./en.json                              -> "en"
//   ./oportal.shell.frontend/fr-CA.json    -> "fr-CA"
// Files whose stem isn't a supported locale are silently skipped (defensive —
// today there are none, but it keeps stray files from breaking the build).
//
// We deep-merge so namespace files that share a top-level key (e.g. `logout`)
// contribute their own sub-keys instead of overwriting each other. Leaf-value
// collisions (same key path, both strings) still resolve as last-write-wins,
// and load order across folders is not guaranteed — avoid relying on it.
for (const [path, contents] of loadLocaleFiles()) {
    const match = path.match(/([^/]+)\.json$/);
    if (!match) {
        continue;
    }
    const locale = match[1];
    if (!isSupported(locale)) {
        continue;
    }
    deepMerge(messages[locale], contents);
}
export function getBrowserLocale() {
    return navigator.language || "en";
}
